import { CloseIcon } from 'assets/icons/CloseIcon'
import { PlusIcon } from 'assets/icons/PlusIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { addInventoryTransfer } from 'features/inventory/inventoryTransfersSlice'
import useModalClose from 'hooks/useModalClose'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Button, CursorPointer, Flex, ItemsGrid, ModalScrollContainer, SquareIcon } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'

export const InventoryTransferModal = ({ closeModal }: { closeModal: any }) => {
    const [state, setState] = useState<IInventoryTransferModal>({
        transactionDate: null,
        fromLocation: null,
        toLocation: null,
        lines: [
            { item: null, quantity: null, id: new Date().getMilliseconds() }
        ]
    })
    const dispatch = useDispatch<AppDispatch>()
    useFocusFirstInput()
    useModalClose(closeModal)

    const handleSave = async () => {
        const resp = await dispatch(addInventoryTransfer(state))
        if (resp.meta.requestStatus === 'fulfilled') {
            closeModal()
        }
    }

    const handleSelect = (value: ISelectValue, name: string) => {
        setState({ ...state, [name]: value })
    }

    const addLine = () => {
        setState(prev => ({ ...prev, lines: [...prev.lines, { id: new Date().getMilliseconds(), item: null, quantity: null }] }))
    }

    const handleSelectItem = (value: ISelectValue, name: string, id: number) => {
        setState(prev => ({
            ...prev, lines: prev.lines.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, [name]: value }
                }
            })
        }))
    }

    const removeLine = (id: number) => {
        setState(prev => ({
            ...prev, lines: prev.lines.filter(b => b.id !== id)
        }))
    }

    const handleItemQuantity = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const { valueAsNumber } = e.target
        setState(prev => ({
            ...prev, lines: prev.lines.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, quantity: valueAsNumber }
                }
            })
        }))
    }

    const isDisabled = useMemo(() => {
        return !state.fromLocation?.value || !state.toLocation?.value || state.lines.length === 0 || state.lines.some(l => !l.item || !l.quantity)
    }, [state])

    return <Modal black>
        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold' >Add Transfer</TitleText>
            <CursorPointer onClick={closeModal} >
                <CloseIcon />
            </CursorPointer>
        </Flex>
        <ModalScrollContainer>
            <Flex column between center >
                <Flex column gap='1rem' width='100%' >
                    <ItemsGrid columns={2}>
                        <CalendarInput
                            labelText='Transaction Date'
                            defaultValue={state.transactionDate}
                            name='transactionDate'
                            onChange={(v) => setState(prev => ({ ...prev, transactionDate: v }))}
                            nomarg
                            width='100%'
                        />
                        <HonestSelectAsyncInput
                            nomarg
                            link='locations'
                            placeholder='Search locations'
                            name='fromLocation'
                            labelText='From Location'
                            defaultValue={state?.fromLocation?.label}
                            onChange={handleSelect}
                            parserFunction={parseLocationRegistryOptions}
                            invalid={!state.fromLocation?.value}
                            required
                        />
                        <HonestSelectAsyncInput
                            nomarg
                            link='locations'
                            placeholder='Search locations'
                            name='toLocation'
                            labelText='To Location'
                            defaultValue={state?.toLocation?.label}
                            onChange={handleSelect}
                            parserFunction={parseLocationRegistryOptions}
                            required
                            invalid={!state.toLocation?.value}
                        />
                    </ItemsGrid>

                    <Flex gap='3rem' center  >
                        <BodyText weight='bold' >Items</BodyText>
                        <SquareIcon style={{ marginTop: 16 }} onClick={addLine}  >
                            {PlusIcon}
                        </SquareIcon>
                    </Flex>
                    <Flex column gap='1rem' mb='16'>
                        {state.lines.map(l => <Flex gap='1rem' key={l.id} >
                            <HonestSelectAsyncInput
                                name='item'
                                labelText='Item'
                                nomarg
                                onChange={(v, n) => handleSelectItem(v, n, l.id)}
                                link='inventory/item/stock' // TODO ITEM
                                additionalParams={{ locationId: state.fromLocation?.value }}
                                defaultValue={l.item?.label}
                                parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                                required
                                invalid={!l.item?.value}
                            />
                            <Input placeholder='55' labelText='Quantity' value={l.quantity} type='number' name='quantity' onChange={e => handleItemQuantity(e, l.id)} nomarg required invalid={!l.quantity} />
                            {
                                state.lines.length > 1 && <CursorPointer onClick={() => removeLine(l.id)} >
                                    <CloseIcon />
                                </CursorPointer>
                            }
                        </Flex>)}
                    </Flex>


                </Flex>
            </Flex>
        </ModalScrollContainer>
        <Flex width='100%' justcenter mt='16' >
            <Button disabled={isDisabled} orange onClick={handleSave} >Save</Button>
        </Flex>
    </Modal>
}

export interface IInventoryTransferModal {
    id?: number
    transactionDate: Date
    fromLocation: ISelectValue
    toLocation: ISelectValue
    lines: { id?: number, item: ISelectValue, quantity: number }[]
}